import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiGet } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { asArray, asRecord } from "@/lib/type-utils";

function priceForSource(prices: Record<string, unknown>[], source: string): number | null {
  const entry = prices.find((p) => String(p.source) === source);
  const value = entry ? Number(entry.yes_price) : NaN;
  return Number.isFinite(value) ? value : null;
}

export function PredictitPage() {
  const marketsQuery = useQuery({
    queryKey: ["predictit", "markets"],
    queryFn: () => apiGet<unknown>("/api/predictit/markets"),
    refetchInterval: 300_000,
  });

  // Polymarket-vs-PredictIt matcher. (/api/external/comparison is a
  // single-market Kalshi/asset tool and never returns PredictIt rows.)
  const comparisonQuery = useQuery({
    queryKey: ["predictit", "comparison"],
    queryFn: () => apiGet<unknown>("/api/cross-market/compare"),
    refetchInterval: 300_000,
  });

  // Envelope: {success, count, markets: [...]}
  const markets = asArray(asRecord(marketsQuery.data).markets);
  // Envelope: {success, ..., matches: [{primary_question, price_range_pct, prices: [...]}]}
  const matches = asArray(asRecord(comparisonQuery.data).matches);

  const comparisons = matches.map((match) => {
    const prices = asArray(match.prices);
    const polyPrice = priceForSource(prices, "polymarket");
    const predictitPrice = priceForSource(prices, "predictit");
    const rangePct = Number(match.price_range_pct);
    const divergence = Number.isFinite(rangePct)
      ? rangePct
      : polyPrice !== null && predictitPrice !== null
        ? Math.abs(polyPrice - predictitPrice) * 100
        : 0;
    return {
      question: String(match.primary_question ?? "Unknown"),
      polyPrice,
      predictitPrice,
      divergence,
    };
  });

  const avgDivergence =
    comparisons.length > 0
      ? comparisons.reduce((sum, c) => sum + c.divergence, 0) / comparisons.length
      : 0;

  return (
    <div className="space-y-4 md:space-y-6">
      {marketsQuery.isError || comparisonQuery.isError ? (
        <QueryErrorState
          what="PredictIt market data"
          onRetry={() => {
            if (marketsQuery.isError) void marketsQuery.refetch();
            if (comparisonQuery.isError) void comparisonQuery.refetch();
          }}
        />
      ) : null}

      <Panel
        title="PredictIt Markets"
        description="US-regulated political prediction market comparison with Polymarket."
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard
            label="Active Markets"
            value={marketsQuery.isSuccess ? String(markets.length) : "—"}
            hint="PredictIt markets tracked"
          />
          <StatCard
            label="Avg Divergence"
            value={comparisonQuery.isSuccess ? `${avgDivergence.toFixed(2)}%` : "—"}
            hint="Price difference"
          />
          <StatCard
            label="Opportunities"
            value={comparisonQuery.isSuccess ? String(comparisons.filter((c) => c.divergence > 5).length) : "—"}
            hint=">5% divergence"
          />
        </div>
      </Panel>

      <Panel title="Market Comparison" description="Polymarket vs PredictIt price comparison">
        <div className="max-h-[500px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Market</TableHead>
                <TableHead>Polymarket</TableHead>
                <TableHead>PredictIt</TableHead>
                <TableHead>Divergence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-4 text-center text-slate-400">
                    {comparisonQuery.isError ? "Comparison data unavailable" : "No comparison data available"}
                  </TableCell>
                </TableRow>
              ) : (
                comparisons.slice(0, 20).map((comp, index) => (
                  <TableRow key={index}>
                    <TableCell>{comp.question}</TableCell>
                    <TableCell className="font-medium text-gold-400">{asPercent(comp.polyPrice)}</TableCell>
                    <TableCell className="font-medium text-cyan-400">{asPercent(comp.predictitPrice)}</TableCell>
                    <TableCell className={`font-medium ${comp.divergence > 10 ? "text-green-400" : comp.divergence > 5 ? "text-yellow-400" : "text-slate-400"}`}>
                      {comp.divergence.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <Panel title="PredictIt Markets" description="Active markets on PredictIt">
        <div className="max-h-[400px] space-y-2 overflow-y-auto">
          {markets.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              {marketsQuery.isError ? "PredictIt market data unavailable" : "No PredictIt market data available"}
            </div>
          ) : (
            markets.slice(0, 20).map((market, index) => {
              // _format_market shape: {name, short_name, status, contracts:
              // [{name, mid_price, ...}]} — prices live inside contracts.
              const contracts = asArray(market.contracts);
              const topContract = contracts[0];
              const midPrice = topContract ? Number(topContract.mid_price) : NaN;
              const yesPrice = Number.isFinite(midPrice) ? midPrice : null;
              const noPrice = yesPrice !== null ? 1 - yesPrice : null;
              return (
                <div key={index} className="rounded-lg bg-gunmetal/50 p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {String(market.name ?? market.short_name ?? "Unknown")}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-slate-400">
                        {contracts.length > 1 ? (
                          <span>{contracts.length} contracts · top: {String(topContract?.short_name ?? topContract?.name ?? "—")}</span>
                        ) : null}
                        <span>Yes: {yesPrice !== null ? asPercent(yesPrice) : "—"}</span>
                        <span>No: {noPrice !== null ? asPercent(noPrice) : "—"}</span>
                        {/* PredictIt's API exposes no volume field — never fabricate one. */}
                        <span>Vol: —</span>
                      </div>
                    </div>
                    <span className="rounded-full bg-gold-500/20 px-2 py-1 text-xs text-gold-400">
                      {String(market.status ?? "Unknown")}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="Markets API" description="Raw PredictIt market data">
          <JsonView value={marketsQuery.data} />
        </Panel>
        <Panel title="Comparison API" description="Raw cross-market comparison data">
          <JsonView value={comparisonQuery.data} />
        </Panel>
      </div>
    </div>
  );
}
